import { useState, useEffect } from "react"
import { KeyRound, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils/helpers"
import { COMM_FIELD_BASE, COMM_LABEL } from "@/lib/styles/companyEditTabShell"
import { useUserRole } from "@/hooks/useUserRole"
import { postCompanyUpdate } from "@/features/company/services/companyService"
import { showSuccess, showError, formatApiError } from "@/lib/utils/notifications"
import { isCompanyDuplicateAddressCancelledError } from "@/features/company/utils/companyDuplicateAddress"
import { buildIntegrationsCompanyPayload } from "@/features/company/utils/companyIntegrationPayload"
import { SectionCard } from "@/components/ui/SectionCard"
import companyFormStyles from "../../styles/companyAdd.module.css"
import tabStyles from "../../styles/companyInfoTab.module.css"

import type { IntegrationsTabProps } from "../../types"

const FIELD_CLASS = cn(
    COMM_FIELD_BASE,
    "h-10 w-full",
)

export function IntegrationsTab({
    company,
    onUpdate,
    companyId: companyIdFromRoute,
    clearExitSaveIntent,
    completeExitNavigate,
    markIntegrationsTabSavedClean,
}: IntegrationsTabProps) {
    const { hasPolicy } = useUserRole()
    const [isGenerating, setIsGenerating] = useState(false)

    const [formData, setFormData] = useState({
        activate_api: String(company?.activate_api ?? ""),
        invitation_api_key: String(company?.invitation_api_key ?? ""),
    })

    useEffect(() => {
        setFormData({
            activate_api: String(company?.activate_api ?? ""),
            invitation_api_key: String(company?.invitation_api_key ?? ""),
        })
    }, [company])

    const canEditIntegrations = hasPolicy(["company_integration_edit"])
    const canEditApiKey = hasPolicy(["company_edit_api_key"])
    const canGenerateKey = hasPolicy(["company_generate_api_key"])

    const handleGenerateKey = async () => {
        if (!canEditIntegrations || !canEditApiKey) return
        const postId = company?.company_id ?? company?.id ?? company?.fk_company_id ?? companyIdFromRoute
        if (postId == null || String(postId).trim() === "") {
            showError("Company id is missing; cannot generate a token.")
            return
        }
        setIsGenerating(true)
        try {
            const data = await postCompanyUpdate(postId, { generate_key: 1 })
            if (data !== undefined && data !== null && data !== "") {
                const token = typeof data === "string" ? data : String(data)
                setFormData((prev) => ({ ...prev, invitation_api_key: token }))
            }
        } catch (error: unknown) {
            showError(formatApiError(error))
        } finally {
            setIsGenerating(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!canEditIntegrations) {
            clearExitSaveIntent?.()
            return
        }
        try {
            await onUpdate(
                buildIntegrationsCompanyPayload(company as Record<string, unknown> | undefined, formData),
            )
            showSuccess("Company Updated  Successfully!")
            markIntegrationsTabSavedClean?.()
            completeExitNavigate?.()
        } catch (error: unknown) {
            if (isCompanyDuplicateAddressCancelledError(error)) {
                clearExitSaveIntent?.()
                return
            }
            showError(formatApiError(error))
            clearExitSaveIntent?.()
        }
    }

    return (
        <form
            data-testid="company-integrations-tab-form"
            onSubmit={handleSubmit}
            className={cn(
                tabStyles.tabContent,
                !canEditIntegrations && "pointer-events-none opacity-75",
            )}
            data-tab-id="integrations"
            aria-disabled={!canEditIntegrations}
        >
            <SectionCard
                icon={KeyRound}
                title="Invitation API"
                testId="company-integrations-invitation-api-section"
            >
                <div
                    className={cn(
                        "grid grid-cols-1 items-end gap-6 md:grid-cols-[minmax(0,14rem)_minmax(0,20rem)_auto]",
                        !canEditApiKey && "pointer-events-none opacity-75",
                    )}
                >
                    <div className="w-full min-w-0">
                        <label htmlFor="company-integrations-activate-api-select" className={COMM_LABEL}>
                            Activate API
                        </label>
                        <select
                            id="company-integrations-activate-api-select"
                            data-testid="company-integrations-activate-api-select"
                            value={formData.activate_api}
                            onChange={(e) => setFormData((prev) => ({ ...prev, activate_api: e.target.value }))}
                            className={FIELD_CLASS}
                            disabled={!canEditIntegrations}
                        >
                            <option value="">Select Type</option>
                            <option value="1">Yes</option>
                            <option value="0">No</option>
                        </select>
                    </div>

                    <div className="w-full min-w-0">
                        <label htmlFor="company-integrations-invitation-token-input" className={COMM_LABEL}>
                            Invitation API Token
                        </label>
                        <input
                            id="company-integrations-invitation-token-input"
                            data-testid="company-integrations-invitation-token-input"
                            type="text"
                            value={formData.invitation_api_key}
                            readOnly
                            placeholder="API Token"
                            className={cn(
                                FIELD_CLASS,
                                "cursor-not-allowed bg-slate-50 text-slate-700",
                            )}
                        />
                    </div>

                    {canGenerateKey ? (
                        <div className="w-full md:w-auto">
                            <button
                                type="button"
                                data-testid="company-integrations-generate-token-button"
                                onClick={handleGenerateKey}
                                disabled={isGenerating || !canEditIntegrations || !canEditApiKey}
                                className={cn(companyFormStyles.btnPrimary, "min-w-[160px] gap-2")}
                            >
                                {isGenerating ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Generating...
                                    </>
                                ) : (
                                    "Generate New Token"
                                )}
                            </button>
                        </div>
                    ) : null}
                </div>
            </SectionCard>

            {/* Header Save → Confirm → requestSubmit(); must remain a submit control (no visible Save on this tab). */}
            <button
                type="submit"
                id="integrations-hidden-submit"
                data-testid="company-integrations-hidden-submit"
                tabIndex={-1}
                aria-hidden
                disabled={!canEditIntegrations}
                style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
            />
        </form>
    )
}
